import { useMutation, useQueryClient } from '@tanstack/react-query';
import { UserKeys, betaUpdateSendAs } from '../../../../../api/users-api';
import { handleErrors } from '../../../../../utils/errors';
import { SendAsModel } from '../../../../../../types';
import SharedDialog from '../../../../../shared/shared-dialog';
import { Controller, useForm } from 'react-hook-form';
import { useStore } from '../../../../state/zustand-store';
import { useAnalytics } from '../../../../../shared/analytics/create-events';
import * as analytics from '../../../../../shared/analytics/analytics.json';
import { ModuleList } from '../../../../../shared/analytics/module-list';
import { Messages } from '../../../../../utils/message-constants';

import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';

interface EditSendAsProps {
  open: boolean;
  handleClose: () => void;
  userEmail: string;
  sendAsAccounts: SendAsModel[];
  selectedAccount: SendAsModel;
}

export default function EditSendAsDialog({
  open,
  handleClose,
  userEmail,
  sendAsAccounts,
  selectedAccount,
}: EditSendAsProps) {
  const { createEvent } = useAnalytics();
  const queryClient = useQueryClient();
  const { setSnackbar } = useStore();

  interface FormInput {
    sendAsEmail: string;
    displayName: string;
    replyToAddress: string;
    isDefault: boolean;
    treatAsAlias: boolean;
    isPrimary: boolean;
  }

  const {
    handleSubmit,
    reset,
    control,
    formState: { isDirty, errors },
  } = useForm<FormInput>({
    values: {
      sendAsEmail: selectedAccount.sendAsEmail,
      displayName: selectedAccount.displayName,
      replyToAddress: selectedAccount.replyToAddress,
      isDefault: selectedAccount.isDefault,
      treatAsAlias: selectedAccount.treatAsAlias,
      isPrimary: selectedAccount.isPrimary,
    },
  });

  const onSubmit = (data: FormInput) => {
    mutate(data as SendAsModel);
  };

  const { mutate, isPending } = useMutation({
    mutationFn: (data: SendAsModel) => betaUpdateSendAs(userEmail, data),
    onSuccess: (data) => {
      setSnackbar({
        open: true,
        severity: 'success',
        message: Messages.Success.Updated('Send As account'),
      });

      let newList = [] as SendAsModel[];
      const currentDefault = sendAsAccounts.find(
        (account) => account.isDefault
      );
      if (
        currentDefault &&
        currentDefault.sendAsEmail !== data.sendAsEmail &&
        data.isDefault !== false
      ) {
        currentDefault.isDefault = false;
        newList = sendAsAccounts.filter(
          (account) =>
            account.sendAsEmail != currentDefault.sendAsEmail &&
            account.sendAsEmail != data.sendAsEmail
        );
        newList.push(currentDefault, data);
      } else {
        newList = sendAsAccounts.filter(
          (account) => account.sendAsEmail != data.sendAsEmail
        );
        newList.push(data);
      }

      queryClient.setQueryData([UserKeys.SendAsAccounts, userEmail], newList);

      handleClose();
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage: Messages.Errors.Network.Updating(
          'the selected send as account'
        ),
      });
    },
    onSettled: () => {
      createEvent(
        analytics.user_management.user_details.gmail_settings
          .update_send_as_email,
        {
          module: ModuleList.modules.userManagement.name,
          section: ModuleList.modules.userManagement.sections.sendAs,
        }
      );
    },
  });

  return (
    <SharedDialog
      open={open}
      handleClose={() => {
        handleClose();
        reset();
      }}
      title="Edit Send As Account"
      dialogContent={
        <Stack gap={2} component="form">
          <TextField disabled value={selectedAccount.sendAsEmail} fullWidth />
          <Controller
            name={'displayName'}
            control={control}
            render={({ field }) => (
              <TextField
                autoFocus
                id="displayName"
                label="Name"
                {...field}
                fullWidth
                autoComplete="off"
              />
            )}
          />
          <Controller
            name={'replyToAddress'}
            control={control}
            rules={{
              pattern: {
                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                message: 'Must be a valid email address',
              },
            }}
            render={({ field }) => (
              <Box>
                <TextField
                  {...field}
                  fullWidth
                  id="replyAddress"
                  label="Reply Address"
                  autoComplete="off"
                  error={!!errors.replyToAddress}
                  helperText={
                    errors.replyToAddress?.message
                      ? errors.replyToAddress.message
                      : ''
                  }
                />
              </Box>
            )}
          />
          <Controller
            name="treatAsAlias"
            control={control}
            render={({ field }) => (
              <FormControlLabel
                control={<Checkbox {...field} checked={field.value} />}
                label="Treat as an alias"
              />
            )}
          />
          <Controller
            name="isDefault"
            control={control}
            render={({ field }) => (
              <FormControlLabel
                control={<Checkbox {...field} checked={field.value} />}
                label="Make this the default Send As account"
                disabled={selectedAccount.isPrimary}
              />
            )}
          />
        </Stack>
      }
      primaryButton={{
        loading: isPending,
        action: handleSubmit(onSubmit),
        disabled: isPending || !isDirty,
        title: 'Save Changes',
      }}
    />
  );
}
